/**
 * SSO callback landing page.
 *
 * The browser lands here after the IdP → Keycloak → SPA redirect chain:
 *   https://<org>.epr.urbint.com/sso/callback?code=<kc-code>&state=<state>
 *
 * Responsibilities:
 *   1. Read ``code`` and ``state`` from the URL.
 *   2. POST them to ``/auth/sso/callback`` (browser sends the si_sso_state
 *      cookie automatically; backend verifies state + exchanges code).
 *   3. On success: refresh user/org context, navigate to the intended
 *      destination (persisted to sessionStorage by the Login page) or "/".
 *   4. On failure: toast the error and bounce back to /login.
 *
 * No tokens are visible to JavaScript at any point — the backend sets
 * si_at and si_rt as HttpOnly cookies via Set-Cookie headers on the
 * /sso/callback response. We only see ``expires_in`` + ``user`` in the
 * JSON body.
 */

import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { authApi } from "@/api/services";
import { isApiError } from "@/api/client";
import { useApiToken } from "@/components/providers/token-provider";
import { getOrgName } from "@/config/organization";
import { SSO_REDIRECT_KEY, sanitizeRedirectPath } from "@/lib/auth";

// Module-level guard keyed on the state token. Unlike useRef, module-level
// state is NOT reset by React 18 StrictMode's intentional unmount-remount
// cycle — which resets refs before the async exchange resolves, causing a
// second identical request that fails with 400 (state already consumed).
const _exchangedStates = new Set<string>();

export default function SSOCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useApiToken();

  useEffect(() => {
    const state = searchParams.get("state");
    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    // Handle an IdP/Keycloak error redirect FIRST, before the state guard.
    // Keycloak bounces back with ?error=access_denied when the user is not
    // provisioned (no matching realm user) or hasn't verified their email.
    // A denied response may omit `state`, so checking it after the state
    // guard would leave the user stuck on the spinner. Dedupe on the error
    // (keyed off state when present) so StrictMode's double-mount doesn't
    // double-toast.
    if (errorParam) {
      const errorKey = `error:${state ?? errorParam}`;
      if (_exchangedStates.has(errorKey)) return;
      _exchangedStates.add(errorKey);
      toast.error(
        errorDescription ||
          "Your account isn't set up for SSO yet, or your email isn't verified. " +
            "Check your inbox for a verification link, or contact your administrator.",
      );
      navigate("/login", { replace: true });
      return;
    }

    if (!state || _exchangedStates.has(state)) return;
    _exchangedStates.add(state);

    if (!code) {
      toast.error("Invalid SSO callback — missing code.");
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        await authApi.ssoCallback({
          code,
          state,
          org_slug: getOrgName(),
        });

        // Hydrate user/org state from /auth/me. The cookies set on the
        // callback response are now in the jar; the next request will
        // carry them automatically.
        await refreshUser();

        // Honour the deep-link the user originally tried to reach. Set
        // by the Login page just before triggering the SSO redirect.
        // Sanitized: the stored value is replayed into navigate(), so an
        // attacker-seeded external target must collapse to "/".
        const redirectTo = sanitizeRedirectPath(
          sessionStorage.getItem(SSO_REDIRECT_KEY),
        );
        sessionStorage.removeItem(SSO_REDIRECT_KEY);
        _exchangedStates.delete(state);
        navigate(redirectTo, { replace: true });
      } catch (err: unknown) {
        // A 400 "state expired" on a second call usually means the first
        // call already succeeded and consumed the state (React Router
        // navigation race causes a fresh mount while cookies are being
        // committed). Recover by checking if the session is now live.
        if (isApiError(err) && err.status === 400) {
          const alreadyAuthenticated = await refreshUser().catch(() => false);
          if (alreadyAuthenticated) {
            const redirectTo = sanitizeRedirectPath(
              sessionStorage.getItem(SSO_REDIRECT_KEY),
            );
            sessionStorage.removeItem(SSO_REDIRECT_KEY);
            _exchangedStates.delete(state);
            navigate(redirectTo, { replace: true });
            return;
          }
        }
        // Log a minimal, non-sensitive breadcrumb only. console.error is
        // forwarded to Datadog logs, and the raw error object can embed
        // auth-flow request metadata (callback URL with code/state, headers)
        // that must stay inside the auth boundary.
        console.error(
          "SSO callback failed",
          isApiError(err) ? `status=${err.status ?? "unknown"}` : "unknown error",
        );
        toast.error(
          isApiError(err) ? err.message : "Sign-in failed. Please try again.",
        );
        _exchangedStates.delete(state);
        navigate("/login", { replace: true });
      }
    })();
    // All deps (searchParams, navigate, refreshUser) are intentionally
    // excluded: this effect must run exactly once per mount. The exchange
    // is single-use and re-running it would fail (state + code consumed).
    // Module-level _exchangedStates handles the StrictMode double-mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-sm">Signing you in…</p>
      </div>
    </div>
  );
}
